import type { LooplayInitParams, LooplaySDKCreateOptions } from './types';
import { LooplayAuth } from './auth/looplay-auth';
import { ApiClient } from './apps/api-client';
import type { EmitGameEventResponse, GameEventKey } from './apps/LooplaySDK.types';
import { MissingBaseUrlError, NotInitializedError } from './errors';

export class LooplaySDK {
  readonly auth?: LooplayAuth;
  readonly api?: ApiClient;

  private gameId?: string;
  private initialized = false;

  constructor(options: LooplaySDKCreateOptions = {}) {
    const { auth, baseUrl, fetch, defaultHeaders } = options;

    if (auth) {
      this.auth = new LooplayAuth(auth);
    }

    if (baseUrl) {
      this.api = new ApiClient({
        baseUrl,
        fetch,
        defaultHeaders,
        getAccessToken: async () => this.auth?.getAccessToken(),
      });
    }
  }

  async init(params: LooplayInitParams): Promise<void> {
    this.gameId = params.gameId;
    await this.auth?.init();
    await this.detectIntegration(params);
    await this.verifyGameId(params);
    this.initialized = true;
  }

  async getAccessToken(): Promise<string | undefined> {
    return this.auth?.getAccessToken();
  }

  getGameId(): string | undefined {
    return this.gameId;
  }

  async getMyProfile(): Promise<any> {
    this.assertInitialized();
    if (!this.api) throw new MissingBaseUrlError();
    return this.api.getMyProfile();
  }

  /** Call once when the game view opens, before any play/match tracking. */
  async trackView(): Promise<boolean> {
    this.assertInitialized();
    if (!this.api) throw new MissingBaseUrlError();
    if (!this.gameId) throw new NotInitializedError();
    return this.api.trackView(this.gameId);
  }

  /** Starts a new play session; call when the user presses Play. Returns the play session id. */
  startPlaySession(): string {
    this.assertInitialized();
    if (!this.api) throw new MissingBaseUrlError();
    return this.api.startPlaySession();
  }

  async trackPlay(playTimeSeconds?: number): Promise<boolean> {
    this.assertInitialized();
    if (!this.api) throw new MissingBaseUrlError();
    if (!this.gameId) throw new NotInitializedError();
    return this.api.trackPlay(this.gameId, playTimeSeconds);
  }

  /**
   * Report that a match/round ended.
   * Maps to PLAY_MATCH (and WIN_MATCH when isWin=true).
   * matchId is the idempotency anchor — safe to call twice with the same matchId.
   */
  async trackMatch(
    matchId: string,
    opts: { durationSeconds: number; isCompleted?: boolean; isWin?: boolean }
  ): Promise<boolean> {
    this.assertInitialized();
    if (!this.api) throw new MissingBaseUrlError();
    if (!this.gameId) throw new NotInitializedError();
    return this.api.trackMatch(this.gameId, {
      matchId,
      matchDurationSeconds: opts.durationSeconds,
      isCompleted: opts.isCompleted,
      isWin: opts.isWin,
    });
  }

  /**
   * Emit a custom action event (CUSTOM_ACTION metric).
   * Use GameEventKey for platform-defined codes; custom strings for game-specific quests.
   * Pass refId to make the call idempotent — same refId = same event, dedup-safe.
   */
  async emit(
    actionCode: GameEventKey | string,
    opts?: { value?: number; refId?: string; payload?: Record<string, unknown> }
  ): Promise<EmitGameEventResponse> {
    this.assertInitialized();
    if (!this.api) throw new MissingBaseUrlError();
    if (!this.gameId) throw new NotInitializedError();
    return this.api.emit(this.gameId, actionCode, opts);
  }

  private assertInitialized(): void {
    if (!this.initialized) throw new NotInitializedError();
  }

  private async verifyGameId(params: LooplayInitParams): Promise<void> {
    if ((params.verifyMode ?? 'none') === 'none') return;
    // strict: resolves gameId (the game's public appId) via a read-only,
    // unauthenticated call — throws if the game doesn't exist or isn't LIVE.
    if (!this.api) throw new MissingBaseUrlError();
    await this.api.listGameAssets(params.gameId);
  }

  private async detectIntegration(params: LooplayInitParams): Promise<void> {
    if (!this.api) return;
    await this.api.detectIntegration(params.gameId);
  }
}
